// ─────────────────────────────────────────────────────────────────────────────
// frame_decorations.ts — Per-page frames around the body content rect
//
// A frame delimits the writing area (the body content box, i.e. the
// "line-width body"), NOT the outer page border. Used to give the
// content a clear bound — useful with templates, calendars, or just
// for visual structure.
//
// Each entry draws into the same content rect (the page's marginal
// inset). Adding a new frame is one REGISTRY entry; the renderer is
// agnostic to which style is chosen.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageLayout } from './page_renderer';

export interface FrameDecoration {
  id: string;
  label: string;
  draw(ctx: CanvasRenderingContext2D, layout: PageLayout): void;
}

function contentBox(L: PageLayout): { x: number; y: number; w: number; h: number } {
  return {
    x: L.x + L.marginLeft,
    y: L.y + L.marginTop,
    w: L.width - L.marginLeft - L.marginRight,
    h: L.height - L.marginTop - L.marginBottom,
  };
}

// ── Built-ins ───────────────────────────────────────────────────────────────

const NONE: FrameDecoration = {
  id: 'none',
  label: 'Plain',
  draw: () => { /* nothing */ },
};

/** Simple frame — single hairline rectangle around the body content. */
const SIMPLE: FrameDecoration = {
  id: 'simple',
  label: 'Simple frame',
  draw(ctx, L) {
    const b = contentBox(L);
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.55)';
    ctx.lineWidth = 0.7;
    ctx.strokeRect(b.x, b.y, b.w, b.h);
    ctx.restore();
  },
};

/** Thick frame — heavier sepia line with a small inner offset hairline,
 *  giving a "matted" or "passe-partout" feel around the content. */
const THICK: FrameDecoration = {
  id: 'thick',
  label: 'Thick frame',
  draw(ctx, L) {
    const b = contentBox(L);
    ctx.save();
    ctx.strokeStyle = 'rgba(58, 32, 16, 0.65)';
    ctx.lineWidth = 1.8;
    ctx.strokeRect(b.x, b.y, b.w, b.h);
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.45)';
    ctx.lineWidth = 0.6;
    const inset = 5;
    ctx.strokeRect(b.x + inset, b.y + inset, b.w - inset * 2, b.h - inset * 2);
    ctx.restore();
  },
};

/** Single-left frame — only the left side of the body content rect,
 *  acting as a clean writing margin rule (replaces the retired
 *  hardcoded journal red line). */
const SINGLE_LEFT: FrameDecoration = {
  id: 'single-left',
  label: 'Single (left)',
  draw(ctx, L) {
    const b = contentBox(L);
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.55)';
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x, b.y + b.h);
    ctx.stroke();
    ctx.restore();
  },
};

/** Single-top frame — only the top edge. */
const SINGLE_TOP: FrameDecoration = {
  id: 'single-top',
  label: 'Single (top)',
  draw(ctx, L) {
    const b = contentBox(L);
    ctx.save();
    ctx.strokeStyle = 'rgba(110, 80, 50, 0.55)';
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x + b.w, b.y);
    ctx.stroke();
    ctx.restore();
  },
};

// ── Registry ────────────────────────────────────────────────────────────────

const REGISTRY: Map<string, FrameDecoration> = new Map();
function register(d: FrameDecoration): void { REGISTRY.set(d.id, d); }

register(NONE);
register(SIMPLE);
register(THICK);
register(SINGLE_LEFT);
register(SINGLE_TOP);

export function getFrameDecoration(id: string | undefined | null): FrameDecoration {
  if (!id) return NONE;
  return REGISTRY.get(id) ?? NONE;
}

export function listFrameDecorations(): FrameDecoration[] {
  return [...REGISTRY.values()];
}
